import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Phone, MessageCircle, AtSign, MapPin, Clock, Send, CheckCircle, Calendar } from 'lucide-react';
import { BRAND, HOURS } from '../utils/constants';
import { saveLead } from '../utils/leads';
import useSEO from '../hooks/useSEO';
import './Contact.css';

const PACKAGE_OPTIONS = [
  { value: '', label: 'Select a package...' },
  { value: 'online', label: 'Online Training — $10,000/month' },
  { value: 'inperson', label: 'In-Person Training — $15,000/month' },
  { value: 'dropin', label: 'Drop-In Classes — $1,500/class' },
  { value: 'unsure', label: 'Not sure yet — help me decide' },
];

export default function Contact() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const preselect = params.get('package') || '';

  const contactSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    mainEntity: {
      '@type': 'LocalBusiness',
      name: 'The Fhysique Factory',
      telephone: '+18767713011',
      email: 'fhysiquefactoryltd@gmail.com',
      address: { '@type': 'PostalAddress', streetAddress: '6-8 Parkington Plaza, Maths Unlimited Building', addressLocality: 'Kingston', addressCountry: 'JM' },
      geo: { '@type': 'GeoCoordinates', latitude: '17.9972', longitude: '-76.7928' },
      openingHoursSpecification: [
        { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday','Tuesday','Thursday','Friday'], opens: '05:00', closes: '20:15' },
        { '@type': 'OpeningHoursSpecification', dayOfWeek: 'Wednesday', opens: '05:00', closes: '19:00' },
        { '@type': 'OpeningHoursSpecification', dayOfWeek: 'Saturday', opens: '07:00', closes: '10:00' },
      ],
    },
  }), []);

  const breadcrumbSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://fysiquefactory.fit/' },
      { '@type': 'ListItem', position: 2, name: 'Contact', item: 'https://fysiquefactory.fit/contact' },
    ],
  }), []);

  useSEO({
    title: 'Contact The Fhysique Factory | Book Personal Trainer Kingston Jamaica',
    description: 'Contact Ramone Fearon at The Fhysique Factory, Parkington Plaza Kingston Jamaica. Call +1 (876) 771-3011, WhatsApp, or fill out the enquiry form. Book your free consultation for online or in-person personal training.',
    path: '/contact',
    schema: [contactSchema, breadcrumbSchema],
  });

  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    packageInterest: preselect,
    message: '',
  });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setForm((f) => ({ ...f, packageInterest: preselect }));
  }, [preselect]);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Name is required';
    if (!form.phone.trim()) e.phone = 'Phone number is required';
    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email)) e.email = 'Invalid email';
    return e;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setSubmitting(true);
    // Save to local CRM
    saveLead({ ...form, source: 'contact-form' });
    fetch('/api/send-contact', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name:            form.name,
        phone:           form.phone,
        email:           form.email || '',
        packageInterest: form.packageInterest || '',
        message:         form.message || '',
      }),
    }).catch(() => {});
    setSubmitted(true);
    setSubmitting(false);
  };

  const handleChange = (ev) => {
    const { name, value } = ev.target;
    setForm((f) => ({ ...f, [name]: value }));
    if (errors[name]) setErrors((e) => ({ ...e, [name]: undefined }));
  };

  return (
    <div className="page contact-page">
      <section className="page-hero">
        <div className="container">
          <span className="section-label">Get Started</span>
          <div className="lime-line" />
          <h1 className="section-title">Contact & Book</h1>
          <p className="section-sub">
            Ready to transform? Reach out now — we respond within hours.
          </p>
        </div>
      </section>

      <section className="contact-main">
        <div className="container contact-grid">
          {/* Left: Form + Booking */}
          <div className="contact-left">
            {/* Booking CTA */}
            <div className="booking-card">
              <div className="booking-card-header">
                <Calendar size={22} className="text-lime" />
                <h3>Book a 1-on-1 Consultation</h3>
              </div>
              <p>
                Get a private 60-minute session with Ramone — full assessment, personalized program,
                and sample meal plan. <strong className="text-lime">$2,000 JMD</strong> (credited toward any monthly plan).
              </p>
              <Link to="/book?package=consultation" className="btn btn-primary btn-full">
                <Calendar size={16} /> Book &amp; Pay Online — $2,000
              </Link>
              <p className="booking-alt">
                Prefer to talk first?{' '}
                <a
                  href={`https://wa.me/${BRAND.whatsapp}?text=Hi! I'd like to book a 1-on-1 consultation with Ramone.`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-lime"
                >
                  WhatsApp us
                </a>{' '}
                or{' '}
                <a href={`tel:${BRAND.phone}`} className="text-lime">call directly</a>.
              </p>
            </div>

            {/* Contact Form */}
            {submitted ? (
              <div className="form-success">
                <CheckCircle size={48} className="text-lime" />
                <h3>Message Received!</h3>
                <p>
                  Thanks <strong>{form.name}</strong>! We'll contact you at{' '}
                  <strong>{form.phone}</strong> within a few hours.
                </p>
                <p className="text-muted" style={{ fontSize: '0.9rem' }}>
                  Can't wait? WhatsApp or call us directly below.
                </p>
                <a
                  href={`https://wa.me/${BRAND.whatsapp}?text=Hi! I just filled out your contact form. My name is ${form.name}.`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-whatsapp"
                >
                  <MessageCircle size={16} /> WhatsApp Now
                </a>
              </div>
            ) : (
              <form className="contact-form" onSubmit={handleSubmit} noValidate>
                <h3>Send a Message</h3>
                <p className="form-sub text-muted">We'll get back to you within hours.</p>

                <div className={`form-group ${errors.name ? 'error' : ''}`}>
                  <label>Full Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Your full name"
                  />
                  {errors.name && <span className="form-error">{errors.name}</span>}
                </div>

                <div className={`form-group ${errors.phone ? 'error' : ''}`}>
                  <label>Phone Number *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    placeholder="+1 (876) 000-0000"
                  />
                  {errors.phone && <span className="form-error">{errors.phone}</span>}
                </div>

                <div className={`form-group ${errors.email ? 'error' : ''}`}>
                  <label>Email (optional)</label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                  />
                  {errors.email && <span className="form-error">{errors.email}</span>}
                </div>

                <div className="form-group">
                  <label>Package Interest</label>
                  <select name="packageInterest" value={form.packageInterest} onChange={handleChange}>
                    {PACKAGE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Message (optional)</label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    placeholder="Tell us about your goals, availability, or any questions..."
                    rows={4}
                  />
                </div>

                <button type="submit" className="btn btn-primary btn-full" disabled={submitting}>
                  {submitting ? 'Sending...' : <><Send size={16} /> Send Message</>}
                </button>
              </form>
            )}
          </div>

          {/* Right: Contact info */}
          <div className="contact-right">
            <h3>Reach Us Directly</h3>
            <p className="text-muted" style={{ marginBottom: '24px' }}>
              The fastest way to get started is a direct message or call.
            </p>

            <div className="contact-btns">
              <a
                href={`https://wa.me/${BRAND.whatsapp}?text=Hi! I'm interested in joining The Physique Factory.`}
                target="_blank"
                rel="noopener noreferrer"
                className="contact-btn whatsapp"
              >
                <MessageCircle size={22} />
                <div>
                  <span>WhatsApp</span>
                  <small>{BRAND.phoneDisplay}</small>
                </div>
              </a>

              <a href={`tel:${BRAND.phone}`} className="contact-btn call">
                <Phone size={22} />
                <div>
                  <span>Call Now</span>
                  <small>{BRAND.phoneDisplay}</small>
                </div>
              </a>

              <a
                href={BRAND.instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="contact-btn instagram"
              >
                <AtSign size={22} />
                <div>
                  <span>Instagram DM</span>
                  <small>@{BRAND.instagram}</small>
                </div>
              </a>
            </div>

            {/* Location */}
            <div className="info-block">
              <h4><MapPin size={16} className="text-lime" /> Location</h4>
              <p>{BRAND.location}</p>
              <a href={BRAND.mapsUrl} target="_blank" rel="noopener noreferrer" className="map-link">
                Open in Google Maps →
              </a>
            </div>

            {/* Hours */}
            <div className="info-block">
              <h4><Clock size={16} className="text-lime" /> Hours</h4>
              <ul className="hours-list">
                {HOURS.map((h) => (
                  <li key={h.day}>
                    <span>{h.day}</span>
                    <span className={h.time === 'Closed' ? 'closed' : ''}>{h.time}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Map embed placeholder */}
            <div className="map-embed">
              <iframe
                title="Physique Factory Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d28632445.842002183!2d-100.00575118586983!3d28.833750362308027!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8edb41626229243f%3A0x1eef204fc3542358!2sThe%20Fhysique%20Factory%20Gym!5e0!3m2!1sen!2sjm!4v1775241297895!5m2!1sen!2sjm"
                width="100%"
                height="200"
                style={{ border: 0, borderRadius: 'var(--radius)', filter: 'invert(90%) hue-rotate(180deg)' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
